import { CardData, ButtonTextData } from '@Types/common';
import React, { useRef, useState, useEffect } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import { AnalyticsParamBuilder, validateUrl } from '@Utils/common';
import ImageUploadComponent from '../ImageUploadComponent';
import { useGenerateShortURLMutation } from '@Pages/AutomationBuilderV0/apiSlice';
import posthog from 'posthog-js';



interface CardEditorProps {
  index: number;
  automationId: any;
  data?: any;
  update_response_data: (data: any) => void;
  // showMobileMockupCallback: (ssmm: boolean) => void;
}

const COMPONENT = "card_editor";

const CardEditor: React.FC<CardEditorProps> = ({ automationId, data, update_response_data }) => {
  const [cards, setCards] = useState<CardData[]>(data?.data?.cards || [])
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [isEditing, setIsEditing] = useState<number>(-1);
  const [showMobileMockup, setShowMobileMockup] = useState(false);
  const swiperRef = useRef<SwiperType | null>(null);
  const [cardErrors, setCardErrors] = useState<{ [index: number]: any }>([]);

  const [generateShortURL, { isLoading: generateShortURLIsLoading }] = useGenerateShortURLMutation()

  const [tempButton, setTempButton] = useState<ButtonTextData | null>(null);
  const [tempButtonErrors, setTempButtonErrors] = useState<{ title?: string; url?: string }>({});



  useEffect(() => {
    // showMobileMockupCallback(showMobileMockup);
  }, [showMobileMockup]);

  useEffect(() => {
    setTempButton(null);
    setIsEditing(-1);
  }, [activeIndex]);


  const validateCard = (card: CardData): any => {
    const errors: any = {};

    if (card.title.length > 80) errors['title'] = 'Title exceeds 80 characters';
    if (card.subtitle.length > 80) errors['subtitle'] = 'Subtitle exceeds 80 characters';
    if (card.image_url && !validateUrl(card.image_url)) errors['image'] = 'Invalid image URL';

    const buttonErrors: any[] = [];

    card.buttons.forEach((btn, i) => {
      const btnErrors: any = {};

      if (btn.title.length > 20) {
        btnErrors['title'] = `Button ${i + 1} text too long`;
      }

      if (btn.type === 'link' && btn.data && !validateUrl(btn.data)) {
        btnErrors['url'] = `Button ${i + 1} has invalid URL`;
      }

      if (Object.keys(btnErrors).length > 0) {
        buttonErrors[i] = btnErrors;
      }
    });

    if (buttonErrors.length > 0) {
      errors['button'] = buttonErrors;
    }

    return errors;
  };



  useEffect(() => {

    for (let i = 0; i < cards.length; i++) {
      const errors = validateCard(cards[i]);
      setCardErrors(prev => ({ ...prev, [i]: errors }));
    }

    update_response_data({
      ...data,
      data: {
        ...data.data,
        cards: cards,
      }
    })

  }, [cards])



  const updateCardAndValidate = (index: number, newCard: CardData) => {
    const newCards = [...cards];
    newCards[index] = newCard;
    setCards(newCards);
    const errors = validateCard(newCard);
    setCardErrors(prev => ({ ...prev, [index]: errors }));
  };

  const handleCardChange = (index: number, field: keyof CardData, value: string) => {
    const newCard = { ...cards[index], [field]: value };
    updateCardAndValidate(index, newCard);
  };

  const handleButtonChange = (cardIndex: number, buttons: ButtonTextData[]) => {
    const newCard = { ...cards[cardIndex], buttons };
    updateCardAndValidate(cardIndex, newCard);
  };

  const addNewCard = () => {
    posthog.capture("ui_action", AnalyticsParamBuilder({
      kingdom: COMPONENT,
      phylum: "add_new_card",
      genus: cards.length,
      int_value: automationId,
    }));
    const newCard: CardData = {
      title: '',
      subtitle: '',
      image_url: '',
      buttons: [],
    };
    setCards([...cards, newCard]);
    setActiveIndex(cards.length);
  };

  const deleteCard = (indexToDelete: number) => {
    posthog.capture("ui_action", AnalyticsParamBuilder({
      kingdom: COMPONENT,
      phylum: "delete_card",
      classValue: `${indexToDelete}`,
      genus: cards.length,
      int_value: automationId,
    }));
    if (cards.length <= 1) return;
    const newCards = cards.filter((_, index) => index !== indexToDelete);
    setCards(newCards);
    if (activeIndex === indexToDelete) {
      setActiveIndex(indexToDelete === 0 ? 0 : indexToDelete - 1);
    } else if (activeIndex > indexToDelete) {
      setActiveIndex(activeIndex - 1);
    }
  };

  const handleCardButtonUpdate = async () => {
    posthog.capture("ui_action", AnalyticsParamBuilder({
      kingdom: COMPONENT,
      phylum: "card_button",
      classValue: "save_button",
      genus: `${activeIndex}_${isEditing}`,
      int_value: automationId,
      str_value: JSON.stringify(cards[activeIndex].buttons)

    }));
    if (isEditing == -1) return;
    const postData = {
      automation_id: automationId,
      original_url: cards[activeIndex]?.buttons?.[isEditing]?.data
    }

    try {
      const resp = await generateShortURL(postData).unwrap();
      if (resp) {
        const newButtons = [...cards[activeIndex]?.buttons];
        newButtons[isEditing] = { ...newButtons[isEditing], short_url: resp?.short_url };
        handleButtonChange(activeIndex, newButtons);
      }
    } catch (error: any) {
      console.error(error)
    }

    setIsEditing(-1)
  }

  const validateButton = (btn: ButtonTextData): any => {
    const errors: any = {};

    if (btn.title.length > 20) {
      errors.title = 'Button text must be ≤ 20 characters';
    } else if (btn.title.length == 0) {
      errors.title = 'Button text title is empty';
    }

    if (btn.type === 'link' && (!btn.data || !validateUrl(btn.data))) {
      errors.url = 'Invalid URL';
    }

    return errors;
  };

  const handleTempButtonSave = async () => {
    posthog.capture("ui_action", AnalyticsParamBuilder({
      kingdom: COMPONENT,
      phylum: "card_button",
      classValue: "save_button",
      genus: `${activeIndex}_${isEditing}`,
      int_value: automationId,
      str_value: JSON.stringify(tempButton)
    }));
    if (!tempButton) return;

    const validationErrors = validateButton(tempButton);
    if (Object.keys(validationErrors).length > 0) {
      setTempButtonErrors(validationErrors);
      return;
    }

    // Generate and attach short URL for link buttons before saving
    let shortUrl: string | undefined = undefined;
    if (tempButton.type === 'link' && tempButton.data) {
      try {
        const resp = await generateShortURL({
          automation_id: automationId,
          original_url: tempButton.data
        }).unwrap();
        shortUrl = resp?.short_url;
      } catch (error) {
        console.error(error);
      }
    }

    const newButtons = [...cards[activeIndex].buttons, { ...tempButton, short_url: shortUrl }];
    handleButtonChange(activeIndex, newButtons);
    setTempButton(null);
    setTempButtonErrors({});
    setIsEditing(-1);
  }

  const onShowMobileMockup = () => {
    posthog.capture("ui_action", AnalyticsParamBuilder({
      kingdom: COMPONENT,
      phylum: "show_mobile_mockup",
      classValue: !showMobileMockup,
      int_value: automationId,
    }));
    // setShowMobileMockup(!showMobileMockup)
  }

  const OnTextBlurAnalytics = (input_component: string, text: string, type: string) => {
    posthog.capture("ui_input", AnalyticsParamBuilder({
      kingdom: COMPONENT,
      phylum: input_component,
      classValue: type,
      str_value: text,
      int_value: automationId,
    }));
  }

  const OnSelectCard = (index: number) => {
    posthog.capture("ui_action", AnalyticsParamBuilder({
      kingdom: COMPONENT,
      phylum: "select_card",
      classValue: `${index}`,
      int_value: automationId,
    }));
    setActiveIndex(index);
    swiperRef.current?.slideTo(index);
  }

  const onAddButtonClick = (activeIndex: number) => {
    posthog.capture("ui_action", AnalyticsParamBuilder({
      kingdom: COMPONENT,
      phylum: "card_button",
      classValue: "add_button",
      genus: cards[activeIndex].buttons.length,
      int_value: automationId,
    }));
    setTempButton({ title: '', data: '', type: 'link' });
    setIsEditing(cards[activeIndex].buttons.length); // Index it's *going* to be
  }

  const OnEditButtonClick = (idx: number) => {
    posthog.capture("ui_action", AnalyticsParamBuilder({
      kingdom: COMPONENT,
      phylum: `card_button`,
      classValue: "button_text_edit_icon",
      genus: `${activeIndex}_${idx}`,
      int_value: automationId,
    }));
    setIsEditing(idx)
  }

  const OnDeleteButtonClick = (idx: number) => {
    posthog.capture("ui_action", AnalyticsParamBuilder({
      kingdom: COMPONENT,
      phylum: "card_button",
      classValue: "button_text_delete_icon",
      genus: `${activeIndex}_${idx}`,
      int_value: automationId,
      str_value: JSON.stringify(cards[activeIndex].buttons)
    }));

    const newButtons = cards[activeIndex].buttons.filter((_, i) => i !== idx);
    handleButtonChange(activeIndex, newButtons);
  }

  const OnButtonEditCancel = () => {
    posthog.capture("ui_action", AnalyticsParamBuilder({
      kingdom: COMPONENT,
      phylum: "card_button",
      classValue: "button_text_cancel",
      genus: `${activeIndex}_${isEditing}`,
      int_value: automationId,
      str_value: JSON.stringify(cards[activeIndex].buttons)
    }));
    setIsEditing(-1)
  }

  const OnTempButtonEditCancel = () => {
    posthog.capture("ui_action", AnalyticsParamBuilder({
      kingdom: COMPONENT,
      phylum: "card_button",
      classValue: "button_text_cancel",
      genus: `${activeIndex}_temp`,
      int_value: automationId,
      str_value: JSON.stringify(tempButton)
    }));
    setTempButton(null);
    setIsEditing(-1);
    setTempButtonErrors({})
  }


  const OnSlideChange = (swiper: SwiperType) => {
    posthog.capture("ui_action", AnalyticsParamBuilder({
      kingdom: COMPONENT,
      phylum: "select_card",
      classValue: `${swiper.realIndex}`,
      int_value: automationId,
    }));
    setActiveIndex(swiper.realIndex)
  }


  return (
    <div className="space-y-4 border border-grey-300 p-3 rounded-lg my-3">
      {/* Toggle switch */}
      {/* <div className="flex justify-end pr-4">
        <label className="flex items-center gap-2 text-sm font-medium text-gray-600">
          Show Mobile Mockup
          <input
            type="checkbox"
            checked={showMobileMockup}
            onChange={onShowMobileMockup}
            className="toggle toggle-sm toggle-primary"
          />
        </label>
      </div> */}

      {/* Layout */}
      <div className="flex items-center gap-2 flex-wrap">
        {cards.map((_, index) => {
          const hasError = cardErrors[index] ? Object.keys(cardErrors[index]).length > 0 : 0;
          return (
            <div key={index} className=" flex items-center gap-1">
              <button
                onClick={() => OnSelectCard(index)}
                className={`relative w-8 h-8 flex items-center justify-center  ${index === activeIndex ? 'bg-[#3535d5] text-white' : 'bg-white text-gray-600'}`}
              >
                {index + 1}
                {hasError && (
                  <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-red-500"></span>
                )}
              </button>

              {cards.length > 1 && (
                <button
                  onClick={() => deleteCard(index)}
                  className="text-gray-400 hover:text-red-500"
                >
                  ✕
                </button>
              )}
            </div>
          );
        })}

        {cards.length < 10 && (
          <button
            onClick={addNewCard}
            className="w-8 h-8 bg-gray-100 text-gray-600 flex items-center justify-center hover:bg-gray-200"
          >
            +
          </button>
        )}
      </div>
      <div className="flex flex-col md:flex-row gap-4">
        {/* Editor */}
        <div className={`w-full ${showMobileMockup && "md:w-1/2"} space-y-4 mx-auto`}>
          {/* Navigation */}


          {/* Form */}
          <div className="space-y-4 bg-white rounded-lg p-6 border">
            <div>

              <ImageUploadComponent
                value={cards[activeIndex]?.image_url || ''}
                onChange={(imageUrl) => handleCardChange(activeIndex, 'image_url', imageUrl)}
                onBlur={() => OnTextBlurAnalytics(`card_${activeIndex}_image_url`, cards[activeIndex]?.image_url, "image_url")}
                automationId={automationId}
                cardIndex={activeIndex}
                component="card_editor"
              />
              {cardErrors[activeIndex]?.image && (
                <p className="text-sm text-red-500 mt-1">Please enter a valid image URL</p>
              )}


            </div>

            {/* CARD Title */}
            <div className='relative'>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <input
                type="text"
                value={cards[activeIndex]?.title || ''}
                onChange={(e) => handleCardChange(activeIndex, 'title', e.target.value)}
                onBlur={() => OnTextBlurAnalytics(`card_${activeIndex}_title`, cards[activeIndex]?.title, "title")}
                placeholder="Enter title"
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <span className="absolute right-2 bottom-2  text-xs text-gray-400">
                {cards[activeIndex]?.title.length}/80
              </span>
              {cardErrors[activeIndex]?.title && (
                <p className="text-sm text-red-500 mt-1">Title must be ≤ 80 characters</p>
              )}

            </div>


            {/* CARD Subtitle */}
            <div className='relative'>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
              <input
                type="text"
                value={cards[activeIndex]?.subtitle || ''}
                onChange={(e) => handleCardChange(activeIndex, 'subtitle', e.target.value)}
                onBlur={() => OnTextBlurAnalytics(`card_${activeIndex}_subtitle`, cards[activeIndex]?.subtitle, "subtitle")}
                placeholder="Enter subtitle"
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <span className="absolute right-2 bottom-2  text-xs text-gray-400">
                {cards[activeIndex]?.subtitle.length}/80
              </span>
              {cardErrors[activeIndex]?.subtitle && (
                <p className="text-sm text-red-500 mt-1">Subtitle must be ≤ 80 characters</p>
              )}

            </div>

            {/* Buttons */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Buttons</label>
              {cards[activeIndex]?.buttons.map((btn, idx) => (
                <div key={idx} className="space-y-1">
                  {isEditing === idx ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="flex items-center justify-center gap-2">
                          <div className="relative w-full">
                            <input
                              value={btn.title}
                              onChange={(e) => {
                                const newButtons = [...cards[activeIndex].buttons];
                                newButtons[idx] = { ...btn, title: e.target.value };
                                handleButtonChange(activeIndex, newButtons);
                              }}
                              onBlur={() => OnTextBlurAnalytics(`card_${activeIndex}_button_${idx}_title`, btn.title, "title")}
                              placeholder="Enter Button Text"
                              className="py-2 px-4 pr-16 rounded-xl border-2 border-dotted h-[45px] w-full focus:outline-none focus:border-purple-400"
                              maxLength={20}
                            />
                            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                              {btn.title.length}/20
                            </span>
                            {cardErrors[activeIndex]?.button?.[idx]?.title && (
                              <p className="text-sm text-red-500">Button text must be ≤ 20 characters</p>
                            )}
                          </div>
                          <select
                            value={btn.type}
                            onChange={(e) => {
                              const newButtons = [...cards[activeIndex].buttons];
                              newButtons[idx] = { ...btn, type: e.target.value as 'link' | 'postback' };
                              handleButtonChange(activeIndex, newButtons);
                            }}
                            className="h-[45px] px-2 rounded-xl border-2 border-dotted focus:outline-none focus:border-purple-400"
                          >
                            <option value="link">Link</option>
                            {/* <option value="message" disabled>Postback</option> */}
                          </select>
                        </div>

                      </div>
                      {btn.type === 'link' && (
                        <input
                          value={btn.data}
                          onChange={(e) => {
                            const newButtons = [...cards[activeIndex].buttons];
                            newButtons[idx] = { ...btn, data: e.target.value };
                            handleButtonChange(activeIndex, newButtons);
                          }}
                          onBlur={() => OnTextBlurAnalytics(`card_${activeIndex}_button_${idx}_url`, btn.data, "url")}
                          placeholder="Enter URL"
                          className={`w-full px-3 py-2 border rounded-lg ${cardErrors[activeIndex]?.button?.[idx]?.url ? 'border-red-500' : 'focus:ring-2 focus:ring-purple-500'}`}
                        />
                      )}

                      {cardErrors[activeIndex]?.button?.[idx]?.url && (
                        <p className="text-sm text-red-500">Please enter a valid URL</p>
                      )}
                      <div className="flex justify-end gap-2">
                        <button onClick={OnButtonEditCancel} className="text-gray-600">
                          Cancel
                        </button>
                        <button onClick={handleCardButtonUpdate} className="bg-purple-600 text-white px-3 py-1 rounded-lg">
                          {generateShortURLIsLoading && <span className='loading_spinner'></span>}
                          Save
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between p-2 border rounded-xl">
                      <div className='text-center text-sm font-medium text-[#3535d5] w-full'>{btn.title}</div>
                      <div className="flex gap-2">
                        <button onClick={() => OnEditButtonClick(idx)} className="text-gray-500">✎</button>
                        <button
                          onClick={() => OnDeleteButtonClick(idx)}
                          className="text-red-500"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {tempButton && (
                <div className="space-y-1">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="flex items-center justify-center gap-2 w-full">
                        <div className="relative w-full">
                          <input
                            value={tempButton.title}
                            onChange={(e) =>
                              setTempButton({ ...tempButton, title: e.target.value })
                            }
                            onBlur={() => OnTextBlurAnalytics(`card_${activeIndex}_button_${cards[activeIndex].buttons.length}_title`, tempButton.title, "title")}
                            placeholder="Enter Button Text"
                            className="py-2 px-4 pr-16 rounded-xl border-2 border-dotted h-[45px] w-full focus:outline-none focus:border-purple-400"
                            maxLength={20}
                          />
                          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                            {tempButton.title.length}/20
                          </span>
                        </div>
                        <select
                          value={tempButton.type}
                          onChange={(e) =>
                            setTempButton({ ...tempButton, type: e.target.value as 'link' | 'postback' })
                          }
                          className="h-[45px] px-2 rounded-xl border-2 border-dotted focus:outline-none focus:border-purple-400"
                        >
                          <option value="link">Link</option>
                        </select>
                      </div>

                    </div>
                    {tempButtonErrors.title && (
                      <p className="text-sm text-red-500">{tempButtonErrors.title}</p>
                    )}

                    {tempButton.type === 'link' && (
                      <input
                        value={tempButton.data}
                        onChange={(e) => setTempButton({ ...tempButton, data: e.target.value })}
                        onBlur={() => OnTextBlurAnalytics(`card_${activeIndex}_button_${cards[activeIndex].buttons.length}_url`, tempButton.data, "url")}
                        placeholder="Enter URL"
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                      />
                    )}
                    {tempButton.type === 'link' && tempButtonErrors.url && (
                      <p className="text-sm text-red-500">{tempButtonErrors.url}</p>
                    )}

                    <div className="flex justify-end gap-2">
                      <button onClick={OnTempButtonEditCancel} className="text-gray-600">
                        Cancel
                      </button>
                      <button onClick={handleTempButtonSave} className="bg-purple-600 text-white px-3 py-1 rounded-lg">
                        Save
                      </button>
                    </div>



                  </div>
                </div>
              )}


              {cards[activeIndex]?.buttons.length < 3 && (
                <button
                  onClick={() => onAddButtonClick(activeIndex)}
                  disabled={isEditing != -1}
                  className="w-full py-2 px-4 border-2 border-dashed rounded-lg hover:border-purple-400 disabled:border-gray-200 disabled:bg-gray-200 transition"
                >
                  + Add Button
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Mockup */}
        {showMobileMockup && (
          <div className="w-full md:w-1/2 relative">
            <img
              src="https://app.linkdm.com/assets/images/dashboard_mobile_mockup.png"
              className="w-full relative"
              alt="Mobile Mockup"
            />
            <div className="absolute left-[30px] bottom-[20px]  w-[80%] h-[420px] rounded-[20px]">
              <Swiper
                onSwiper={(swiper: SwiperType) => (swiperRef.current = swiper)}
                onSlideChange={(swiper: SwiperType) => OnSlideChange(swiper)}
                loop={false}
                spaceBetween={12}
                slidesPerView={1.45}
                slidesOffsetBefore={-40} // tweak this if needed
                centeredSlides
                onActiveIndexChange={(swiper: SwiperType) => OnSlideChange(swiper)}
              >
                {cards.slice(0, 10).map((card, index) => (
                  <SwiperSlide key={index}>
                    <div
                      className={`w-full h-full m-2 p-2 bg-white rounded-lg shadow ${index === activeIndex ? 'ring-2 ring-purple-500' : ''
                        }`}
                    >
                      {card.image_url ? (
                        <img src={card.image_url} alt="" className="w-full h-40 object-cover rounded-md" />
                      ) : (
                        <div className="w-full h-40 flex items-center justify-center bg-gray-100 text-sm text-gray-500 border rounded-md">
                          No Image
                        </div>
                      )}
                      <div className="mt-2">
                        <h3 className="text-sm font-semibold truncate">{card.title || "Enter Title"}</h3>
                        <p className="text-xs text-gray-500 truncate">{card.subtitle || "Enter Subtitle"}</p>
                      </div>
                      <div className="mt-2 space-y-1">
                        {card.buttons.map((btn, i) => (
                          <button
                            key={i}
                            className="w-full py-1 text-xs text-center border rounded bg-gray-100 hover:bg-gray-200 truncate"
                          >
                            {btn.title || 'Button'}
                          </button>
                        ))}
                        {card.buttons.length < 3 && (
                          <button
                            onClick={() => onAddButtonClick(activeIndex)}
                            className="w-full py-1 text-xs text-center border rounded bg-gray-100 hover:bg-gray-200 truncate"
                          >
                            + Add Button
                          </button>
                        )}
                      </div>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </div>

        )}


      </div>
    </div>
  );
};

export default CardEditor;
